import React, { BaseHTMLAttributes, ReactNode, forwardRef } from "react";

import classNames from "classnames";

import { StyledLink, StyledLinkElement, StyledLinkElements } from "./Styles";
import {
  PolymorphicComponentPropsWithRef,
  PolymorphicRef,
} from "../../utils/types";
import OpenExpandedIcon from "../Icons/collection/OpenExpanded";

export interface ILinkProps extends BaseHTMLAttributes<HTMLElement> {
  /** Children type of node or string */
  children: ReactNode | string;
  /** Variant Buttons */
  variant?:
    | "primary"
    | "secondary"
    | "tertiary"
    | "option"
    | "contained"
    | "outlined"
    | "text colored"
    | "text gray"
    | "dark transparent"
    | "circle raised"
    | "text link-primary"
    | "text link-gray";
  /** Button size can be big, medium or small */
  size?: "big" | "small";
  /** Disabled prop */
  disabled?: boolean;
  /** CSS classes */
  className?: string;
  /** Renders an OpenExpanded icon to the right of children */
  isExternal?: boolean;
}

export type LinkProps<C extends React.ElementType> =
  PolymorphicComponentPropsWithRef<C, ILinkProps>;

export type LinkComponent = <C extends React.ElementType = "a">(
  props: LinkProps<C>
) => React.ReactElement | ReactNode | null;

/**
 * Link component
 */
export const Link: LinkComponent & { displayName?: string } = forwardRef(
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore - due to @node/types making ts more strict
  <C extends React.ElementType = "a">(
    {
      as,
      children,
      variant = "contained",
      size,
      disabled = false,
      className,
      isExternal = false,
      ...args
    }: LinkProps<C>,
    ref?: PolymorphicRef<C>
  ) => {
    const Component = as || "a";

    return (
      <StyledLink
        ref={ref}
        as={Component}
        disabled={disabled}
        className={classNames("c-link", className, {
          "c-link--contained": variant === "primary" || variant === "contained",
          "c-link--outlined": variant === "secondary" || variant === "outlined",
          "c-link--text_colored":
            variant === "tertiary" || variant === "text colored",
          "c-link--text_gray": variant === "option" || variant === "text gray",
          "c-link--dark_transparent": variant === "dark transparent",
          "c-link--circle_raised": variant === "circle raised",
          "c-link--big": size === "big",
          "c-link--small": size === "small",
          "c-link--icon-only":
            children instanceof Object && !(children instanceof Array),
          "c-link--disabled": disabled,
        })}
        variant={variant}
        size={size}
        $iconOnly={children instanceof Object && !(children instanceof Array)}
        {...args}
      >
        {children instanceof Array || isExternal ? (
          <StyledLinkElements className="c-link__elements">
            {(children instanceof Array ? children : [children]).map(
              (value, index) =>
                value ? (
                  <StyledLinkElement
                    className="c-link__elements__element"
                    key={index}
                  >
                    {value}
                  </StyledLinkElement>
                ) : null
            )}
            {isExternal && (
              <StyledLinkElement className="c-link__elements__element">
                <OpenExpandedIcon width={16} height={16} />
              </StyledLinkElement>
            )}
          </StyledLinkElements>
        ) : (
          children
        )}
      </StyledLink>
    );
  }
);

Link.displayName = "Link";
